import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useWebsitePublishState } from '@shared/hooks/useWebsitePublishState';
import { AnalyticsContext, useTranslation } from '@shared/core';
import { useToast, Flex, Box } from '@withjoy/joykit';
import { noop } from 'lodash-es';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { pxToRem } from '@withjoy/joykit/theme';
import { getAbsoluteUrlFromPathname } from '@shared/utils/urlHelpers';
import { useEmailVerificationDialog } from '@shared/components/EmailVerificationDialog/useEmailVerificationDialog';
import { usePublishCrossSell } from '@shared/components/StationeryCrossSell';
// Leaf module, not the barrel: the barrel also pulls in the provider and the lazy panel, which
// would put both into the bundle of all 12 publish surfaces that never open the wizard.
import { useGoLiveChecksContext } from '@shared/components/GoLiveChecks/GoLiveChecks.context';
import { useGoLiveChecksRedirect } from '@shared/components/GoLiveChecks/useGoLiveChecksRedirect';
import type { StationeryCrossSellTelemetrySurface } from '@shared/components/StationeryCrossSell';
import type { GoLiveChecksPublishTelemetry, GoLiveChecksSource } from '@shared/components/GoLiveChecks/GoLiveChecks.types';
import type { GoLiveChecksIntroChoice } from '@shared/components/GoLiveChecks/GoLiveChecksIntro.types';
import type { WebsiteV2NotReadyReason } from '@shared/hooks/useWebsitePublishState.types';
import { GO_LIVE_CHECKS_TELEMETRY_CATEGORY } from '@shared/components/GoLiveChecks/GoLiveChecks.constants';
import { getWebsiteV2PublishExtraInfo } from '@shared/utils/websiteV2/websiteV2PublishTelemetry';

/**
 * How the publish ask opens where Go Live Checks applies:
 * - `intro`: the "Want a quick check before you publish?" intro.
 * - `warning`: the "Publish your site before sharing." warning first, whose Publish then opens the
 *   intro — for a share action (copying a hidden link) that must carry on whatever the couple picks.
 * - `wizard`: straight into the editor's wizard, for a couple who already answered the intro where
 *   they clicked and was redirected here.
 */
type PublishPromptMode = 'intro' | 'warning' | 'wizard';

interface UseUnpublishedWebsiteWarningParams {
  context: string | { category: string; page?: string };
  extraInfo?: Record<string, unknown>;
  /**
   * Opt in to the post-publish stationery cross-sell modal. Defaults to `false`
   * so incidental publishes triggered by send/share flows (messaging, reminders,
   * card/eCard post-checkout, etc.) keep their existing success toast and don't
   * interrupt those flows. Only deliberate "publish my website" surfaces (e.g.
   * the website-editor Publish button) should enable it.
   *
   * Ignored for a publish from the Go Live Checks wizard: its "Your site is live" screen carries
   * the share-and-cards prompt, so the modal would only repeat it. The success toast is skipped
   * there too, for the same reason. The intro's "Publish Without Checking" still gets both.
   *
   * `'intro'` limits it to that intro publish: for a surface whose warning publish is incidental
   * (Website Overview's copy link) but whose intro publish stands in for a deliberate toggle.
   */
  enablePublishCrossSell?: boolean | 'intro';
  /**
   * Opt in to the pre-publish "Go Live Checks" wizard. Only takes effect where a
   * `GoLiveChecksProvider` is mounted *and* `websiteGoLiveChecksEnabled` is on — today that is
   * the website editor alone.
   *
   * Deliberately opt-in per surface. The twelve incidental publishes (finishing a card, sending
   * a message, a reminder) are exactly the flows an eight-step wizard must not interrupt.
   */
  enableGoLiveChecks?: boolean;
  /**
   * Wraps the publish handler the Go Live Checks wizard runs. The website editor uses it to keep
   * the extra work it does on publish (poking the preview message bus) attached to the handler
   * the wizard invokes, rather than the panel reimplementing publish. Ignored when the wizard
   * isn't active.
   *
   * The wrapped handler resolves `true` only on a confirmed publish; pass that through.
   */
  composePublishHandler?: (publish: () => Promise<boolean>) => () => Promise<boolean>;
  /**
   * Opt in to Go Live Checks outside the editor, reported as this source: the publish ask becomes
   * the intro, whose "Check My Site" redirects to the website designer's wizard (see
   * `useGoLiveChecksRedirect`). For deliberate publish surfaces; the send flows leave it off, since
   * a redirect would abandon the send. While the flags load, the warning's Publish stays busy.
   */
  redirectToGoLiveChecks?: GoLiveChecksSource;
  /**
   * Runs before the intro's "Check My Site" leaves this surface for the wizard. Resolve `false` to
   * stay put — Settings does, to ask for a save first and redirect once it lands. The Share dialog
   * uses it to close itself before it's navigated away from.
   */
  onBeforeRedirect?: () => boolean | Promise<boolean>;
  /**
   * Called after a publish from the intro or the warning landed, for the surface's own state.
   * `fromIntro` tells the two apart, e.g. to report the intro publish as the toggle it replaced.
   */
  onPublished?: (info: Readonly<{ fromIntro: boolean }>) => void;
  /** Skip the Go Live Checks redirect lookup, e.g. on a guest site nobody here can publish. */
  skipRedirect?: boolean;
}

export const useUnpublishedWebsiteWarning = (params: UseUnpublishedWebsiteWarningParams) => {
  const {
    context,
    extraInfo = {},
    enablePublishCrossSell = false,
    enableGoLiveChecks = false,
    composePublishHandler,
    redirectToGoLiveChecks,
    onBeforeRedirect,
    onPublished,
    skipRedirect = false,
  } = params;
  const { eventInfo, eventHandle } = useEventInfo();
  const eventId = eventInfo?.eventId || '';
  const { tq } = useTranslation('websiteDesigner');
  const publishState = useWebsitePublishState();
  const { publish: publishPublishState, goToWebsiteEditor, websiteV2SiteId } = publishState;
  const promiseResolveRef = useRef(noop);
  const { toast } = useToast();
  const analytics = useContext(AnalyticsContext);
  const { ensureEmailVerified, dialogProps: emailVerificationDialogProps } = useEmailVerificationDialog();
  // `undefined` everywhere but the website editor, and a plain context read where it is mounted —
  // so the other publish surfaces pay nothing for a wizard they never open.
  const goLiveChecks = useGoLiveChecksContext();
  const isGoLiveChecksActive = enableGoLiveChecks && !!goLiveChecks?.isEnabled;
  const {
    isRedirectEnabled: isGoLiveChecksRedirectEnabled,
    isRedirectLoading: isGoLiveChecksRedirectLoading,
    redirectToGoLiveChecks: redirectToGoLiveChecksWizard,
  } = useGoLiveChecksRedirect({ source: redirectToGoLiveChecks, skip: skipRedirect });
  const isGoLiveChecksRedirectActive = isGoLiveChecksRedirectEnabled && !isGoLiveChecksActive;
  // Go Live Checks applies to this surface: the wizard here in the editor, or the redirect to it from
  // anywhere else. Where it does, the intro — not the warning — is how the couple is asked to publish.
  const isGoLiveChecksOnSurface = isGoLiveChecksActive || isGoLiveChecksRedirectActive;
  // The publish handler the wizard runs. `useEventCallback` keeps one stable identity while always
  // invoking the latest closure, so `openUnpublishedWebsiteWarning` — declared above
  // `publishWebsite` — doesn't churn all 13 call sites every time that handler's deps change.
  const runWizardPublish = useEventCallback(async (goLiveChecksTelemetry?: GoLiveChecksPublishTelemetry) => {
    const publishFromWizard = () => publishWebsite({ viaWizard: true, goLiveChecksTelemetry });
    const publish = composePublishHandler ? composePublishHandler(publishFromWizard) : publishFromWizard;
    return publish();
  });
  // Cross-sell events report against the calling page — same context this hook
  // already uses for its PublishGoLive event. Only admin publish surfaces opt in.
  const contextCategory = typeof context === 'string' ? context : context.category;
  const contextPage = typeof context === 'string' ? undefined : context.page;
  const crossSellSurface = useMemo<StationeryCrossSellTelemetrySurface>(
    () => ({ category: contextCategory, pagePrefix: 'admin', page: contextPage }),
    [contextCategory, contextPage],
  );
  const { handlePublishSuccess, dialogProps: crossSellDialogProps } = usePublishCrossSell(crossSellSurface);

  const [isWarningOpen, setIsWarningOpen] = useState(false);
  // Website V2 with no site it could publish: the warning says why and points at the editor instead.
  // Fixed at open, so a site that finishes generating meanwhile can't swap in a Publish that skipped
  // the email gate.
  const [notReadyReason, setNotReadyReason] = useState<WebsiteV2NotReadyReason | null>(null);
  const [isIntroOpen, setIsIntroOpen] = useState(false);
  const [isIntroChecking, setIsIntroChecking] = useState(false);
  const [isIntroPublishing, setIsIntroPublishing] = useState(false);

  /** Hands the waiting caller its answer. Idempotent: a second call is a no-op. */
  const resolveOpener = useEventCallback(() => {
    promiseResolveRef.current();
    promiseResolveRef.current = noop;
  });

  // The intro reports under the wizard's category, whichever surface opened it: `source` says which.
  // Existing Amplitude events only (`PageViewed`, `DesignButtonInteracted`), as the wizard's own do.
  const introSource = redirectToGoLiveChecks ?? 'websiteDesigner';
  const trackIntroShown = useEventCallback(() => {
    analytics.track({
      category: GO_LIVE_CHECKS_TELEMETRY_CATEGORY,
      action: 'PageViewed',
      label: introSource,
      extraInfo: { name: 'goLiveChecksIntroShown', eventId, source: introSource },
    });
  });
  const trackIntroChoice = useEventCallback((choice: GoLiveChecksIntroChoice) => {
    analytics.track({
      category: GO_LIVE_CHECKS_TELEMETRY_CATEGORY,
      action: 'DesignButtonInteracted',
      actionType: 'click',
      label: choice,
      extraInfo: { name: 'goLiveChecksIntroChoice', eventId, source: introSource, choice },
    });
  });

  const openIntro = useEventCallback(() => {
    setIsIntroOpen(true);
    trackIntroShown();
  });

  /**
   * Opens the editor's wizard; the opener resolves when its session ends, published or not.
   * `withIntro` starts it on the intro, in the wizard's own panel rather than a modal: the couple
   * who picks "Check My Site" carries straight on to the checks in the space they're looking at.
   */
  const openWizard = useEventCallback(
    (withIntro: boolean) =>
      new Promise<void>(resolve => {
        promiseResolveRef.current = resolve;
        if (withIntro) trackIntroShown();
        goLiveChecks?.open({
          onPublish: runWizardPublish,
          onClose: resolveOpener,
          ...(withIntro && {
            intro: {
              onChoice: trackIntroChoice,
              onPublishWithoutChecking: () => publishWithoutChecking(),
            },
          }),
        });
      }),
  );

  useEffect(() => {
    return () => {
      promiseResolveRef.current();
      promiseResolveRef.current = noop;
    };
  }, []);

  /**
   * Opens the unpublished website warning modal if the current event
   * is eligible for website exposure but hasn't been published yet.
   * For Website V2 that's the carbon site, not the legacy flag: a live site never prompts, and an
   * event whose site is still generating (or failed) is told so, with no Publish to offer.
   *
   * Returns a Promise that resolves when the user takes action
   * (either to publish or keep hidden). This allows calling code to
   * await the user's choice before proceeding (e.g., pausing a share or send flow).
   *
   * Where Go Live Checks applies, `mode` picks what opens instead (see `PublishPromptMode`). The
   * public openers below fix it, so a click handler's event can never be read as a mode.
   */
  const openPublishPrompt = useCallback(
    async (mode: PublishPromptMode) => {
      // Not known yet, or not readable: prompt nothing rather than hold up a send on a guess.
      if (publishState.isLoading || publishState.hasLoadError) {
        return Promise.resolve();
      }

      // If feature is enabled and the website isn't published, prompt the user.
      if (publishState.isExposed && !publishState.isPublished) {
        // Nothing to publish, so no email gate and no Go Live Checks: just the way to the editor.
        if (publishState.notReadyReason) {
          const promise = new Promise(resolve => {
            promiseResolveRef.current = resolve;
          });
          setNotReadyReason(publishState.notReadyReason);
          return promise;
        }

        // A redirect arrival already answered the intro where it clicked. The wizard never layers on
        // the warning: `isWarningOpen` stays false, and the opener resolves when the session ends.
        // No `ensureEmailVerified()` ahead of it: verification is the wizard's own first step.
        if (isGoLiveChecksActive && goLiveChecks && mode === 'wizard') {
          return openWizard(false);
        }

        // In the editor the intro is the wizard's first screen, in its panel.
        if (isGoLiveChecksActive && goLiveChecks && mode === 'intro') {
          return openWizard(true);
        }

        // Where Go Live Checks applies, the intro asks — and it runs email verification itself, on
        // "Publish Without Checking" (the wizard does on "Check My Site"). It resolves on the same
        // terms as the warning: when the couple leaves it, so an awaiting share flow still waits.
        if (isGoLiveChecksOnSurface && mode !== 'warning') {
          const promise = new Promise(resolve => {
            promiseResolveRef.current = resolve;
          });
          openIntro();
          return promise;
        }

        // Gate publishing behind email verification before showing the publish dialog.
        // If the user dismisses verification, treat it like "keep hidden" and continue.
        // Skipped where Go Live Checks applies: the warning's Publish leads to the intro, which gates it.
        const verified = isGoLiveChecksOnSurface || (await ensureEmailVerified());
        if (!verified) {
          return;
        }

        // Return a promise that will resolve when the user closes/interacts with the warning.
        const promise = new Promise(resolve => {
          promiseResolveRef.current = resolve;
        });

        setIsWarningOpen(true);

        return promise;
      }

      // Otherwise, resolve immediately (no warning needs to be shown).
      return Promise.resolve();
    },
    [
      publishState.isLoading,
      publishState.hasLoadError,
      publishState.isExposed,
      publishState.isPublished,
      publishState.notReadyReason,
      ensureEmailVerified,
      isGoLiveChecksActive,
      isGoLiveChecksOnSurface,
      goLiveChecks,
      openWizard,
      openIntro,
    ],
  );

  /** The publish ask: the intro where Go Live Checks applies, the warning elsewhere. */
  const openUnpublishedWebsiteWarning = useCallback(() => openPublishPrompt('intro'), [openPublishPrompt]);
  /** For sharing a hidden link: the warning first, even where Go Live Checks applies. */
  const openUnpublishedShareWarning = useCallback(() => openPublishPrompt('warning'), [openPublishPrompt]);
  /** For a redirect arrival in the editor: the wizard, skipping the intro already answered. */
  const openGoLiveChecksWizard = useCallback(() => openPublishPrompt('wizard'), [openPublishPrompt]);

  // Resolves whether the site actually went live. Publish failures are handled here (logged by
  // `useWebsitePublishState`, toasted here) rather than thrown, so callers that care — the Go Live Checks wizard, which records a
  // terminal outcome — need the result reported instead.
  //
  // `viaWizard` and `goLiveChecksTelemetry` are only ever passed by the wizard's publish. Kept off
  // the modal's click handler below so a click event can never land in the telemetry. `fromIntro`
  // marks the intro's "Publish Without Checking".
  const publishWebsite = useCallback(
    async ({
      viaWizard = false,
      goLiveChecksTelemetry,
      fromIntro = false,
    }: Readonly<{ viaWizard?: boolean; goLiveChecksTelemetry?: GoLiveChecksPublishTelemetry; fromIntro?: boolean }> = {}) => {
      const outcome = await publishPublishState();
      if (outcome === 'handedOff') {
        // The page is leaving for the editor's Joy AI Website paywall, which publishes after payment.
        // The opener stays pending: a send resumed now would fire as the page unloads, and the browser
        // would cancel it unseen. The warning stays up too, for a navigation the couple cancels.
        return false;
      }
      if (outcome !== 'published') {
        toast(
          outcome === 'purchaseCheckUnavailable'
            ? tq`We couldn't confirm your Joy AI Website purchase. Try publishing again in a moment.`
            : tq`😬 Failed to publish website. Please try again.`,
        );
        return false;
      }
      // Under Go Live Checks the wizard's "Your site is live" screen is the
      // confirmation, so neither the cross-sell nor the toast shows over it.
      // Otherwise show the cross-sell when this surface opted in (deliberate
      // website publishes), and fall back to the standard "site published"
      // toast. Keeps send/checkout flows uninterrupted.
      const isCrossSellEnabled = enablePublishCrossSell === 'intro' ? fromIntro : enablePublishCrossSell;
      if (!viaWizard && !(isCrossSellEnabled && handlePublishSuccess())) {
        toast(
          <Flex
            alignItems="center"
            gap={5}
          >
            {tq`🥳 Your site has been published.`}
            <Box
              as="a"
              href={getAbsoluteUrlFromPathname(`/${eventHandle}`)}
              target="_blank"
              rel="noopener noreferrer"
              typographyVariant="label4"
              textDecoration="underline"
              textDecorationStyle="dotted"
              fontWeight={500}
              style={{
                textUnderlineOffset: pxToRem(2),
              }}
            >
              {tq`View Your Site →`}
            </Box>
          </Flex>,
        );
      }

      analytics.track({
        category: typeof context === 'string' ? context : context.category,
        action: 'PublishGoLive',
        extraInfo: {
          eventId,
          ...extraInfo,
          // Every publish says whether it came through the Go Live Checks wizard, so publish
          // reporting can split the two without a join; a wizard publish also says how far the
          // couple got. `isGoLiveChecksActive` alone is not enough — it only means this surface
          // would open the wizard, not that this publish came from it.
          viaGoLiveChecks: !!goLiveChecksTelemetry,
          ...(goLiveChecksTelemetry && { goLiveChecks: goLiveChecksTelemetry }),
          // The intro offered the checks and the couple published as is.
          ...(fromIntro && { viaGoLiveChecksIntro: true }),
          // Website V2: which editor, and the site that went live. Adds nothing for V1.
          ...getWebsiteV2PublishExtraInfo(websiteV2SiteId),
        },
      });

      // The warning modal is done once it publishes, so its opener resumes now. The wizard is not:
      // it stays open on "Your site is live", and a share flow awaiting the opener must not resume
      // underneath it. Its session's `onClose` resolves the promise when the couple leaves.
      if (!viaWizard) {
        onPublished?.({ fromIntro });
        resolveOpener();
        setIsWarningOpen(false);
        setIsIntroOpen(false);
      }
      return true;
    },
    [
      publishPublishState,
      enablePublishCrossSell,
      handlePublishSuccess,
      onPublished,
      resolveOpener,
      toast,
      tq,
      eventHandle,
      analytics,
      context,
      eventId,
      extraInfo,
      websiteV2SiteId,
    ],
  );

  // Where Go Live Checks applies, the warning's Publish is a step towards the intro, not a publish:
  // the opener (a link copy) carries on now, as the site's fate is the intro's to settle.
  const handlePublishClick = useCallback(() => {
    if (isGoLiveChecksOnSurface) {
      resolveOpener();
      setIsWarningOpen(false);
      openIntro();
      return Promise.resolve(false);
    }
    return publishWebsite();
  }, [isGoLiveChecksOnSurface, publishWebsite, resolveOpener, openIntro]);

  const handleKeepHiddenClick = useCallback(() => {
    resolveOpener();
    setIsWarningOpen(false);
    setNotReadyReason(null);
  }, [resolveOpener]);

  /**
   * Website V2 with nothing to publish: on to the editor, where the site is finished or fixed. A
   * full navigation, so the opener stays pending and the warning up, as for the paywall hand-off.
   */
  const handleOpenWebsiteEditorClick = useCallback(() => {
    goToWebsiteEditor();
  }, [goToWebsiteEditor]);

  /**
   * The modal's "Check My Site", outside the editor (whose intro lives in the wizard's panel): the
   * surface gets its say first (`onBeforeRedirect`), then the couple is sent to the wizard. The
   * opener resolves before, since the redirect unmounts whoever is awaiting it.
   */
  const handleIntroCheck = useEventCallback(async () => {
    trackIntroChoice('checkMySite');
    setIsIntroChecking(true);
    try {
      const shouldRedirect = onBeforeRedirect ? await onBeforeRedirect() : true;
      resolveOpener();
      setIsIntroOpen(false);
      if (shouldRedirect) redirectToGoLiveChecksWizard();
    } finally {
      setIsIntroChecking(false);
    }
  });

  /**
   * "Publish Without Checking", for the modal and the editor panel's intro alike: the one check it
   * can't skip is email verification, which runs here first — a dismissed verification resolves
   * `false` and leaves the couple on the intro. Then the same in-place publish the warning runs,
   * wrapped as the wizard's is so the editor's preview still refreshes. Resolves whether it went live.
   */
  const publishWithoutChecking = useEventCallback(async () => {
    if (!(await ensureEmailVerified())) return false;
    const publishHere = () => publishWebsite({ fromIntro: true });
    return (composePublishHandler ? composePublishHandler(publishHere) : publishHere)();
  });

  const handleIntroPublishWithoutChecking = useEventCallback(async () => {
    trackIntroChoice('publishWithoutChecking');
    setIsIntroPublishing(true);
    try {
      await publishWithoutChecking();
    } finally {
      setIsIntroPublishing(false);
    }
  });

  /** The way out: the ✕, the backdrop or Escape. The site stays hidden. */
  const handleIntroClose = useEventCallback(() => {
    trackIntroChoice('closed');
    resolveOpener();
    setIsIntroOpen(false);
  });

  const warningProps = useMemo(
    () => ({
      isOpen: isWarningOpen || !!notReadyReason,
      onPublish: handlePublishClick,
      isPublishing: publishState.isUpdating || isGoLiveChecksRedirectLoading,
      onKeepHidden: handleKeepHiddenClick,
      websiteV2NotReady: notReadyReason ? { reason: notReadyReason, onOpenEditor: handleOpenWebsiteEditorClick } : undefined,
      emailVerificationDialogProps,
      crossSellDialogProps,
      intro: {
        isOpen: isIntroOpen,
        onCheck: handleIntroCheck,
        onPublishWithoutChecking: handleIntroPublishWithoutChecking,
        onClose: handleIntroClose,
        isPublishing: isIntroPublishing || publishState.isUpdating,
        isChecking: isIntroChecking,
      },
    }),
    [
      isWarningOpen,
      notReadyReason,
      handlePublishClick,
      handleKeepHiddenClick,
      publishState.isUpdating,
      handleOpenWebsiteEditorClick,
      isGoLiveChecksRedirectLoading,
      emailVerificationDialogProps,
      crossSellDialogProps,
      isIntroOpen,
      handleIntroCheck,
      handleIntroPublishWithoutChecking,
      handleIntroClose,
      isIntroPublishing,
      isIntroChecking,
    ],
  );

  // Whether any of the warning's dialogs is up, so a consumer can mount the lazy warning only then.
  // The cross-sell counts: it opens as the intro or warning closes, and must stay mounted.
  const isWarningShown = isWarningOpen || !!notReadyReason || isIntroOpen || emailVerificationDialogProps.isOpen || crossSellDialogProps.isOpen;

  return {
    openUnpublishedWebsiteWarning,
    openUnpublishedShareWarning,
    openGoLiveChecksWizard,
    warningProps,
    /** Any of its dialogs is open: mount `LazyUnpublishedWebsiteWarning` only while this is true. */
    isWarningShown,
    /** Go Live Checks applies here: a publish toggle should ask with the intro, not publish. */
    isGoLiveChecksOnSurface,
    /** Its flags are still loading: hold a publish toggle, or an early flip publishes past it. */
    isGoLiveChecksLoading: isGoLiveChecksRedirectLoading,
    /** The publish state the warning asks from (Website V2-aware), for a surface that opens it unasked. */
    websitePublishState: publishState,
  };
};
